/**
 * What a lemma's two lines print.
 *
 * The Greek line, from its span state (see the API's `Lemma.validate_span`):
 *
 *     a span          the selected text ("χυδαῖοι ἐγένοντο")
 *     context only    "(πιστεύσεις δὲ)—(ὅτι ἀποδώσει σοι τὸν σπόρον,)": anchored
 *                     to the verse, selecting nothing
 *     an insertion    "(beginning)" / "(end)" at the verse's edges, else "(insertion)"
 *     nothing         "—": a Hebrew-only entry, or a verse the LXX lacks
 *
 * The Hebrew line: its text, else its context around the dash, else nothing
 * (the renderer prints *nullus*).
 *
 * Offsets are UTF-16 code units, which is what a JavaScript string counts.
 */

export type Line = 'greek' | 'hebrew'

/** What a line with no text prints on either side of its dash. */
export interface LineContext {
  before: string | null
  after: string | null
}

/** The lemma's fields holding each line's context (the API's columns). */
export const LINE_CONTEXT_FIELDS = {
  greek: { before: 'greek_context_before', after: 'greek_context_after' },
  hebrew: { before: 'hebrew_context_before', after: 'hebrew_context_after' },
} as const

export type LineContextField = (typeof LINE_CONTEXT_FIELDS)[Line][keyof LineContext]

export const LINE_CONTEXT_KEYS: LineContextField[] = Object.values(LINE_CONTEXT_FIELDS).flatMap((f) => [
  f.before,
  f.after,
])

/** The four context fields; absent when the lemma was never loaded with them. */
export type LineContexts = Partial<Record<LineContextField, string | null>>

/** One line's context, read from the lemma's fields. */
export const lineContext = (lemma: LineContexts, line: Line): LineContext => {
  const fields = LINE_CONTEXT_FIELDS[line]
  return { before: lemma[fields.before] ?? null, after: lemma[fields.after] ?? null }
}

/** The fields that set one line's context. */
export const lineContextFields = (line: Line, context: LineContext): LineContexts => {
  const fields = LINE_CONTEXT_FIELDS[line]
  return { [fields.before]: context.before, [fields.after]: context.after }
}

/** "(before)—(after)", each part only when given; nothing when neither is. */
export const gapLine = (context: LineContext): string | null => {
  const before = (context.before || '').trim()
  const after = (context.after || '').trim()
  if (!before && !after) return null
  return `${before ? `(${before})` : ''}—${after ? `(${after})` : ''}`
}

export interface SpanSource extends LineContexts {
  lemma: string | null
  lemma_start: number | null
  lemma_stop: number | null
}

export const greekLine = (lemma: SpanSource, verse: string | null | undefined): string => {
  if (lemma.lemma) return lemma.lemma

  const gap = gapLine(lineContext(lemma, 'greek'))
  if (gap) return gap

  const { lemma_start: start, lemma_stop: stop } = lemma
  if (typeof start === 'number' && start === stop) {
    if (start === 0) return '(beginning)'
    if (verse && start === verse.length) return '(end)'
    return '(insertion)'
  }

  return '—'
}

/** A lemma as its two lines see it. */
export type LemmaLines = SpanSource & { hebrew?: string | null }

const filled = (v: string | null | undefined): boolean => !!(v || '').trim()

/**
 * Each line's text (the API's `@line_contexts`): whether the lemma has it, and
 * the fields that take it away. The Greek line's text is a selection or an
 * insertion point; the Hebrew line's, its Hebrew.
 */
const LINE_TEXT: Record<Line, { has: (lemma: LemmaLines) => boolean; cleared: Partial<LemmaLines> }> = {
  greek: {
    has: (lemma) => filled(lemma.lemma) || typeof lemma.lemma_start === 'number',
    cleared: { lemma: '', lemma_start: null, lemma_stop: null },
  },
  hebrew: { has: (lemma) => filled(lemma.hebrew), cleared: { hebrew: '' } },
}

/** Whether the line has text of its own. */
export const hasLineText = (lemma: LemmaLines, line: Line): boolean => LINE_TEXT[line].has(lemma)

/** The fields that take a line's text away (its context then stands in). */
export const withoutLineText = (line: Line): Partial<LemmaLines> => ({ ...LINE_TEXT[line].cleared })

/** A line with no text that prints its context around the dash. */
export const isGapLine = (lemma: LemmaLines, line: Line): boolean =>
  !hasLineText(lemma, line) && gapLine(lineContext(lemma, line)) !== null

/**
 * Whether a lemma says what it is (the API's `Lemma.validate_identified`): an
 * insertion point, or its text, its Hebrew, or context on either line.
 */
export const identifiesLemma = (lemma: LemmaLines): boolean =>
  hasLineText(lemma, 'greek') || hasLineText(lemma, 'hebrew') || LINE_CONTEXT_KEYS.some((key) => filled(lemma[key]))

/** Whether Greek text, when there is some, says where it stands (the API's `Lemma.validate_located`). */
export const locatesLemma = (lemma: SpanSource): boolean =>
  !(lemma.lemma || '').trim() || (typeof lemma.lemma_start === 'number' && typeof lemma.lemma_stop === 'number')

/** The Hebrew line's text, or its context around the dash; null prints *nullus*. */
export const hebrewLine = (lemma: LineContexts & { hebrew: string | null }): string | null =>
  lemma.hebrew || gapLine(lineContext(lemma, 'hebrew'))

export default {
  greekLine,
  hebrewLine,
  gapLine,
  identifiesLemma,
  locatesLemma,
  hasLineText,
  isGapLine,
  withoutLineText,
  lineContext,
  lineContextFields,
  LINE_CONTEXT_FIELDS,
  LINE_CONTEXT_KEYS,
}
